import {createSelector} from 'reselect';
import {selectPlatform} from './app.selectors';
import {
    getAvailableDate,
    isCatalogItemUnsupported,
    isCatalogItemUplay,
    getCustomProp,
    getOwnedCatalogItemDLCItems
} from '../../utils/CatalogUtils';
import {catalog} from '../../constants';

export const selectDefaultCurrency = state => state.getIn(['catalog', 'defaultCurrency']);

export const selectCatalogItems = state => state.getIn(['catalog', 'catalogItems']);

export const selectCatalogIdFromProps = (_, props) =>
    props && (props.catalogItemId || props.catalogId || props.catalogid || props.id);

export const selectCatalogItemById = createSelector(
    selectCatalogIdFromProps,
    selectCatalogItems,
    (catalogId, catalogItems) => catalogId && catalogItems && catalogItems.get(catalogId)
);

export const selectIsCatalogItemUnsupported = createSelector(
    selectCatalogIdFromProps,
    selectPlatform,
    selectCatalogItems,
    (catalogId, platform, catalogItems) => {
        if (catalogId === catalog.LAUNCHER) return false; // always supported
        if (!catalogId || !platform || !catalogItems) return true;
        const catalogItem = catalogItems.get(catalogId);
        return isCatalogItemUnsupported(catalogItem, platform);
    }
);

export const selectHasUnreadCatalog = createSelector(
    state => state.getIn(['catalog', 'latestCatalogTimestamp']),
    state => state.getIn(['catalog', 'latestCatalogAcked']),
    (lastestCatalogTimestamp, latestCatalogAcked) => {
        try {
            const latest = new Date(lastestCatalogTimestamp);
            const acked = latestCatalogAcked && new Date(latestCatalogAcked);
            return !acked || latest > acked;
        } catch (e) {
            return false;
        }
    }
);

export const selectIsCatalogItemUplay = createSelector(
    selectCatalogIdFromProps,
    selectCatalogItems,
    (catalogId, catalogItems) => {
        if (!catalogId || !catalogItems) return false;
        const catalogItem = catalogItems.get(catalogId);
        return isCatalogItemUplay(catalogItem);
    }
);

export const selectPartnerLinkId = createSelector(
    selectCatalogIdFromProps,
    selectCatalogItems,
    (catalogId, catalogItems) => {
        if (!catalogId || !catalogItems) return false;
        const catalogItem = catalogItems.get(catalogId);
        return getCustomProp(catalogItem, catalog.customProps.PARTNER_LINK_ID);
    }
);

export const selectAvailableDate = createSelector(
    selectCatalogIdFromProps,
    selectCatalogItems,
    (catalogId, catalogItems) => {
        if (!catalogId || !catalogItems) return false;
        const catalogItem = catalogItems.get(catalogId);
        return getAvailableDate(catalogItem);
    }
);

export const selectIsNeverUpdate = createSelector(
    selectCatalogIdFromProps,
    selectCatalogItems,
    (catalogId, catalogItems) => {
        if (!catalogId || !catalogItems) return false;
        const catalogItem = catalogItems.get(catalogId);
        return !!getCustomProp(catalogItem, catalog.customProps.NEVER_UPDATE);
    }
);

const extraLaunchOptionKeyPattern = /^extraLaunchOption_.*_Name$/;
/**
 * Select extra launch options from the item's custom props. These will be in the format:
 *   extraLaunchOption_001_Name: 'Launch via Oculus'
 *   extraLaunchOption_001_Args: 'args we don't care about in web, launcher handles these'
 *
 * For every properly paired launch option, we need to return the 'normalized' key and the name value to
 * show in the settings dropdown.
 *
 * Launcher accepts the setting via 'extraLaunchOption_001' (without the suffixes) to trigger the launch
 */
export const selectExtraLaunchOptions = createSelector(
    selectCatalogItemById,
    catalogItem => {
        if (!catalogItem) return [];

        const customProps = (catalogItem && catalogItem.customAttributes) || {};
        const customPropKeys = [];

        // Search the custom props for keys matching the extraLaunchOption pattern, and add the appropriate
        // key/name props to the return array if it has a properly paired Args property defined
        for (const key in customProps) {
            if (customProps.hasOwnProperty(key) && extraLaunchOptionKeyPattern.test(key)) {
                const [normalizedKeyName] = key.split(
                    catalog.customProps.EXTRA_LAUNCH_OPTION_NAME_SUFFIX
                );
                // If the extra launch option name has a corresponding args entry, push to the result
                customProps[
                    `${normalizedKeyName}${catalog.customProps.EXTRA_LAUNCH_OPTION_ARGS_SUFFIX}`
                ] &&
                    customPropKeys.push({
                        key: normalizedKeyName,
                        name: customProps[key]
                    });
            }
        }

        // Return the launch options sorted in order of the option key
        // e.g. extraLaunchOption_001, extraLaunchOption_006,  extraLaunchOption_777
        return customPropKeys.sort((a, b) => {
            const keyA = (a && a.key) || '';
            const keyB = (b && b.key) || '';
            return keyA.localeCompare(keyB);
        });
    }
);

export const selectOwnedCatalogDLCItemListById = createSelector(
    selectCatalogItemById,
    selectCatalogItems,
    getOwnedCatalogItemDLCItems
);
